import { useState, useCallback, useEffect, useRef } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { RotateCw, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ImageCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspect?: number;
}

const ASPECT_PRESETS = [
  { label: 'Libre', value: undefined },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:2', value: 3 / 2 },
];

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): Crop {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 80 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  );
}

export const ImageCropDialog = ({
  open,
  onOpenChange,
  imageSrc,
  onCropComplete,
  aspect: initialAspect,
}: ImageCropDialogProps) => {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [rotation, setRotation] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedAspect, setSelectedAspect] = useState<number | undefined>(initialAspect);
  const [safeImageSrc, setSafeImageSrc] = useState('');
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const { toast } = useToast();

  // Reset state + CORS-safe image loading
  useEffect(() => {
    if (open && imageSrc) {
      setCrop(undefined);
      setCompletedCrop(undefined);
      setRotation(0);
      setSelectedAspect(initialAspect);
      setHasUserInteracted(false);
      setImageLoaded(false);

      if (imageSrc.startsWith('blob:') || imageSrc.startsWith('data:')) {
        setSafeImageSrc(imageSrc);
      } else {
        setIsLoadingImage(true);
        fetch(imageSrc)
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.blob();
          })
          .then((blob) => setSafeImageSrc(URL.createObjectURL(blob)))
          .catch((err) => {
            console.error('Failed to load image for cropping:', err);
            toast({ title: 'Erreur de chargement', description: "Impossible de charger l'image.", variant: 'destructive' });
            setSafeImageSrc(imageSrc);
          })
          .finally(() => setIsLoadingImage(false));
      }
    }
  }, [open, imageSrc]);

  const onImageLoaded = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = e.currentTarget;
      const aspect = selectedAspect;
      if (aspect) {
        setCrop(centerAspectCrop(naturalWidth, naturalHeight, aspect));
      } else {
        setCrop({ unit: '%', x: 10, y: 10, width: 80, height: 80 });
      }
      // Mark image as loaded but NOT user-interacted
      setTimeout(() => setImageLoaded(true), 100);
    },
    [selectedAspect]
  );

  const handleAspectChange = (newAspect: number | undefined) => {
    setSelectedAspect(newAspect);
    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      if (newAspect) {
        setCrop(centerAspectCrop(naturalWidth, naturalHeight, newAspect));
      } else {
        setCrop({ unit: '%', x: 10, y: 10, width: 80, height: 80 });
      }
    }
  };

  const getCroppedImg = async (
    image: HTMLImageElement,
    pixelCrop: PixelCrop,
    rot: number
  ): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const cropX = pixelCrop.x * scaleX;
    const cropY = pixelCrop.y * scaleY;
    const cropWidth = pixelCrop.width * scaleX;
    const cropHeight = pixelCrop.height * scaleY;

    const rotRad = (rot * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rotRad));
    const cos = Math.abs(Math.cos(rotRad));

    // Output canvas size after rotation
    const outputWidth = cropWidth * cos + cropHeight * sin;
    const outputHeight = cropWidth * sin + cropHeight * cos;

    canvas.width = Math.round(outputWidth);
    canvas.height = Math.round(outputHeight);

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(rotRad);
    ctx.translate(-cropWidth / 2, -cropHeight / 2);

    // Draw only the cropped area
    ctx.drawImage(
      image,
      cropX, cropY, cropWidth, cropHeight,
      0, 0, cropWidth, cropHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas vide — le recadrage a échoué'));
        },
        'image/jpeg',
        0.92
      );
    });
  };

  const handleSaveCrop = async () => {
    if (!completedCrop || !imgRef.current) {
      toast({ title: 'Aucun recadrage', description: 'Ajustez le cadre avant de valider.', variant: 'destructive' });
      return;
    }

    try {
      setIsProcessing(true);
      const croppedImage = await getCroppedImg(imgRef.current, completedCrop, rotation);
      onCropComplete(croppedImage);
      onOpenChange(false);
    } catch (e) {
      console.error('Error cropping image:', e);
      toast({
        title: 'Erreur de recadrage',
        description: e instanceof Error ? e.message : "Une erreur est survenue.",
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent className="p-0 gap-0 max-w-[95vw] w-full md:w-[900px] max-h-[90vh]">
        <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="px-4 md:px-6 py-3 md:py-4 border-b flex-shrink-0">
            <DialogTitle>Recadrer l'image</DialogTitle>
            <DialogDescription className="mt-1 text-sm">
              Étirez les bords et coins du cadre pour ajuster le recadrage
            </DialogDescription>
          </div>

          {/* Crop Area */}
          <div className="relative flex-1 min-h-[300px] md:min-h-[400px] overflow-auto flex items-center justify-center bg-muted p-4">
            {isLoadingImage ? (
              <div className="flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Chargement...</span>
              </div>
            ) : safeImageSrc ? (
              <ReactCrop
                crop={crop}
                onChange={(c) => {
                  setCrop(c);
                  if (imageLoaded) setHasUserInteracted(true);
                }}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={selectedAspect}
                minWidth={30}
                minHeight={30}
                className="max-h-[50vh] [&_img]:max-h-[50vh] [&_.ReactCrop__crop-selection]:border-[3px] [&_.ReactCrop__drag-handle]:w-5 [&_.ReactCrop__drag-handle]:h-5"
              >
                <img
                  ref={imgRef}
                  src={safeImageSrc}
                  alt="Image à recadrer"
                  onLoad={onImageLoaded}
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    maxWidth: '100%',
                    maxHeight: '50vh',
                    objectFit: 'contain',
                  }}
                  crossOrigin="anonymous"
                />
              </ReactCrop>
            ) : null}
          </div>

          {/* Controls */}
          <div className="flex-shrink-0 border-t bg-background">
            <div className="p-4 md:p-6 space-y-3 md:space-y-4 max-h-[35vh] overflow-y-auto">
              {/* Aspect Ratio Presets */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Format</label>
                <div className="flex flex-wrap gap-2">
                  {ASPECT_PRESETS.map((preset) => (
                    <Button
                      key={preset.label}
                      type="button"
                      variant={selectedAspect === preset.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleAspectChange(preset.value)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Rotation */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <RotateCw className="h-4 w-4" /> Rotation
                  </label>
                  <Button type="button" variant="outline" size="sm" onClick={handleRotate}>
                    Pivoter 90°
                  </Button>
                </div>
                <Slider value={[rotation]} onValueChange={(v) => setRotation(v[0])} min={0} max={360} step={1} className="w-full" />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-3 sticky bottom-0 bg-background">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing} className="min-w-[100px]">
                  Annuler
                </Button>
                <Button type="button" onClick={handleSaveCrop} disabled={isProcessing || isLoadingImage || !hasUserInteracted} className="min-w-[140px]">
                  {isProcessing ? 'Traitement...' : !hasUserInteracted ? 'Ajustez le cadre' : 'Valider le recadrage'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
